<template>
  <div class="selectContact">
    <VSkeletonShimmer template-name="selectPhoneNumber" v-show="isScreenLoading" />

    <div class="selectContact__content" v-show="!isScreenLoading">
      <SelectContactSummary
        class="selectContact__summary"
        :isPhoneContactType="isPhoneContactType"
        :contactList="contactList"
      />

      <SelectContactOptions
        v-if="hasAnyContact"
        class="selectContact__options"
        :selectedContactId.sync="selectedContactId"
        :contactList="contactList"
      />
    </div>

    <SelectContactActions
      v-show="!isScreenLoading"
      class="selectContact__actions"
      :selectedContactId="selectedContactId"
      :isPhoneContactType="isPhoneContactType"
      @confirmContact="confirmContact"
      @toggleContactType="toggleContactType"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import SelectContactActions from '@/views/validationPatient/selectContact/partials/SelectContactActions.partial.vue';
import SelectContactSummary from '@/views/validationPatient/selectContact/partials/SelectContactSummary.partial.vue';
import SelectContactOptions from '@/views/validationPatient/selectContact/partials/SelectContactOptions.partial.vue';
import VSkeletonShimmer from '@/components/skeletonShimmer/VSkeletonShimmer.vue';
import { dataLayersMixin } from '@/utils/mixins';
import datalayerErrorMessage from '@/utils/helpers/message/datalayerErrorMessage.js';
import { HAS_NO_ANY_CONTACT_NUMBER } from '@/utils/constants/datalayerMessage.constants';

export default {
  name: 'SelectContact',
  mixins: [dataLayersMixin],
  components: {
    SelectContactActions,
    SelectContactOptions,
    SelectContactSummary,
    VSkeletonShimmer,
  },
  data() {
    return {
      isScreenLoading: true,
      selectedContactId: null,
    };
  },

  computed: {
    ...mapState({
      contactType: ({ validation }) => validation.data.chosenContactType,
      maskedContacts: ({ mdm }) => mdm.data.maskedContacts,
      mdmPatientId: ({ mdm }) => mdm.data.id,
    }),
    ...mapGetters(['isMobileService']),

    isPhoneContactType() {
      return this.contactType === 'sms';
    },

    hasAnyContact() {
      const hasAnyContact =
        this.contactType === 'sms'
          ? !!this.maskedContacts?.mobilePhoneList.length
          : !!this.maskedContacts?.emailList.length;

      return hasAnyContact;
    },

    contactList() {
      const list =
        this.contactType === 'sms'
          ? this.maskedContacts?.mobilePhoneList || []
          : this.maskedContacts?.emailList;

      return list;
    },

    failScenarioNextRoute() {
      return this.isMobileService
        ? 'irregular_phone_number_mobile_service'
        : 'irregular_phone_number';
    },
  },

  methods: {
    ...mapMutations([
      'enableLoader',
      'SET_VALIDATION_CONTACT_TYPE',
      'SET_VALIDATION_CONTACT_SELECTED',
    ]),
    ...mapActions(['sendValidationToken', 'getMdmContactListById']),

    async toggleScreenLoading() {
      setTimeout(() => {
        this.isScreenLoading = !this.isScreenLoading;
      }, 2000);
    },

    async setContactModel() {
      await this.getContactList();

      if (!this.hasAnyContact) {
        this.goNextStep(
          'check_service_desk',
          datalayerErrorMessage('setContactModel', HAS_NO_ANY_CONTACT_NUMBER),
        );
      }

      this.toggleScreenLoading();
    },

    async getContactList() {
      if (!this.hasAnyContact) {
        await this.getMdmContactListById(this.mdmPatientId).catch(() => {
          this.goNextStep(
            this.failScenarioNextRoute,
            datalayerErrorMessage('getContactList', HAS_NO_ANY_CONTACT_NUMBER),
          );
        });
      }
    },

    async confirmContact() {
      try {
        this.enableLoader(true);

        const hasSucceedValidation = await this.handleValidation();

        this.enableLoader(false);
        if (hasSucceedValidation) {
          this.goNextStep('patient_validation_insert_token');
        }
      } catch (error) {
        console.log(error);
      } finally {
        this.enableLoader(false);
      }
    },

    async handleValidation() {
      if (this.selectedContactId) {
        const selectedContactData = this.contactList.filter((contact) => {
          return Number(this.selectedContactId) === contact.id;
        })[0];

        await this.SET_VALIDATION_CONTACT_SELECTED(selectedContactData);
        let hasSucceedValidation = false;

        try {
          await this.sendValidationToken();
          hasSucceedValidation = true;
        } catch (error) {
          this.$toasted.show('Erro ao enviar o token. Tente novamente.', {
            position: 'top-right',
            duration: 4000,
            className: 'notification--error',
          });
        }

        return hasSucceedValidation;
      }
    },

    async toggleContactType() {
      const newContactType = this.isPhoneContactType ? 'mail' : 'sms';

      await this.SET_VALIDATION_CONTACT_TYPE(newContactType);
      this.getContactList();
    },
  },

  created() {
    this.setContactModel();
  },
};
</script>

<style lang="scss" scoped>
.selectContact {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 0 8px 8px;
  width: 100%;

  &__content {
    min-height: calc(100% - 118px);
  }

  &__summary {
    margin-bottom: 16px;
    margin-top: 30px;
  }

  &__options {
    margin-bottom: 16px;
  }
}
</style>
